<template>
  <v-form @submit.prevent="$emit('save', publication)">
    <v-card>
      <v-card-title>
        {{ $t('edit_publication') }}
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="publication.title"
              :error-messages="errors['title']"
              :label="formMixin_getRequiredFieldLabel($t('title'))"
              name="title"
              autofocus
              @input="formMixin_clearErrors('title')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="publication.secondary_title"
              :error-messages="errors['secondary_title']"
              :label="formMixin_getRequiredFieldLabel($t('secondary_title'))"
              name="secondary_title"
              @input="formMixin_clearErrors('secondary_title')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="publication.url_name"
              :error-messages="errors['url_name']"
              :label="formMixin_getRequiredFieldLabel($t('url_name'))"
              name="url_name"
              @input="formMixin_clearErrors('url_name')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="publication.url_link"
              :error-messages="errors['url_link']"
              :label="formMixin_getRequiredFieldLabel($t('url_link'))"
              :hint="$t('url_hint_text')"
              @input="formMixin_clearErrors('url_link')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-checkbox
              v-model="publication.is_published"
              :error-messages="errors['is_published']"
              :label="$t('published')"
              class="mb-0"
              @change="formMixin_clearErrors('is_published')"
            />

            <v-checkbox
              v-model="publication.show_uploaded_file"
              :error-messages="errors['show_uploaded_file']"
              :label="$t('show_uploaded_file')"
              class="ma-0"
              @change="formMixin_clearErrors('show_uploaded_file')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <template v-if="!publication.image">
              <BaseFileDropZone
                accept=".jpeg,.bmp,.png,.gif,.jpg"
                @drop="onImageUpload($event, 'image', 'App\\Models\\Publication', publication.id)"
              >
                <div
                  class="drop-zone"
                  :class="errors.hasOwnProperty('images.0') ? 'error-outline' : ''"
                >
                  <div class="drop-zone-message">
                    <p>{{ $t('file_drop_zone_message') }}</p>
                  </div>
                </div>
              </BaseFileDropZone>
              <!-- we don't clear error, because we don't know image index.
              Instead error will not be visible once we upload the file -->
              <template v-for="(value, key) in errors">
                <div v-if="key.includes('images')" :key="key">
                  <div
                    v-for="(error, errorIndex) in value"
                    :key="errorIndex"
                    class="mt-2 v-messages theme--light error--text"
                  >
                    {{ error }}
                  </div>
                </div>
              </template>
            </template>

            <div v-else class="pl-10">
              <v-img
                v-if="publication.image"
                :src="$backendUrl(publication.image.image_path)"
                :alt="publication.image.original_name"
                width="100px"
                height="100px"
                class="patient-story-img"
              />
              <div class="d-flex justify-space-between name-box">
                <p v-if="publication.image" class="image-name text-truncate">
                  {{ publication.image.original_name }}
                </p>
                <v-btn icon @click="onImageDelete(publication.image)"
                  ><v-icon>mdi-delete</v-icon></v-btn
                >
              </div>
            </div>
          </v-col>

          <v-col cols="12" sm="6" class="pt-0 pb-0">
            <p class="publish-in-title">
              {{ $t('publish_in') + ': ' }}
            </p>
            <v-checkbox
              class="ma-0"
              v-model="publication.show_in_preterm_birth"
              :error-messages="errors['show_in_preterm_birth']"
              :label="$t('preterm_birth')"
              @change="formMixin_clearErrors('show_in_preterm_birth')"
            />
            <v-checkbox
              class="ma-0"
              v-model="publication.show_in_premature_baby"
              :error-messages="errors['show_in_premature_baby']"
              :label="$t('premature_baby')"
              @change="formMixin_clearErrors('show_in_premature_baby')"
            />
            <v-checkbox
              class="ma-0"
              v-model="publication.show_in_lost_baby"
              :error-messages="errors['show_in_lost_baby']"
              :label="$t('lost_baby')"
              @change="formMixin_clearErrors('show_in_lost_baby')"
            />
            <v-checkbox
              class="ma-0"
              v-model="publication.show_in_already_at_home"
              :error-messages="errors['show_in_already_at_home']"
              :label="$t('already_at_home')"
              @change="formMixin_clearErrors('show_in_already_at_home')"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <BaseFileDropZone
              accept=".jpeg,.jpg,.bmp,.png,.gif,.pdf,.xlsx,.xls,.doc,.docx"
              @drop="
                onFileSelect($event);
                formMixin_clearErrors('file');
              "
            >
              <div v-if="!publication.file" class="drop-zone">
                <div class="drop-zone-message">
                  <p>{{ $t('single_file_upload_msg') }}</p>
                </div>
              </div>

              <div v-else class="drop-zone text-center" :class="errors.file ? 'error-outline' : ''">
                <v-icon class="pt-2" x-large>mdi-file</v-icon>
                <p class="image-name">{{ publication.file.name | truncate(20) }}</p>
                <p class="pt-1">{{ $t('file_replace_message') }}</p>
              </div>
            </BaseFileDropZone>
            <template v-if="errors.file">
              <div
                v-for="(error, errorIndex) in errors.file"
                :key="errorIndex"
                class="mt-2 v-messages theme--light error--text"
              >
                {{ error }}
              </div>
            </template>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <span class="text-subtitle-2 ml-3"> * {{ $t('must_be_filled') }} </span>

        <v-spacer />

        <v-btn text color="secondary" @click="$emit('preview', publication)">{{
          $t('preview')
        }}</v-btn>

        <v-btn text color="primary" @click="$emit('cancel')">
          {{ $t('cancel') }}
        </v-btn>

        <v-btn :disabled="disabled" :loading="disabled" color="primary" text type="submit">
          {{ $t('save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import formMixin from '../mixins/form-mixin';
import BaseFileDropZone from './base/BaseFileDropZone';

export default {
  name: 'PublicationsForm',

  components: { BaseFileDropZone },

  mixins: [formMixin],

  props: {
    publication: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    onFileSelect(file) {
      this.$emit('select:file', file);
    },

    onImageUpload(image, type, modelType, modelId) {
      this.$emit('upload:image', image, type, modelType, modelId);
    },

    onImageDelete(image) {
      this.$emit('delete:image', image);
    },
  },
};
</script>

<style scoped>
.drop-zone {
  background-color: white;
  outline: 2px dashed darkgray;
  width: 100%;
  height: 100px;
  text-align: center;
  margin: auto;
  position: relative;
}
.drop-zone-message {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
}
.drop-zone-message p,
.image-name {
  color: darkgray;
  margin: 0;
}
.name-box {
  width: 120px;
}
.publish-in-title {
  font-size: 1rem;
  color: gray;
  margin-top: 20px;
}
</style>
